export type Coerce = 'money' | 'number' | 'boolean'
export type TypeMap = Record<string, Coerce> // key = field id/path, value = coercion

const moneyRe = /^-?[\d,]+(?:\.\d+)?$/
const numRe   = /^-?(?:\d+|\d*\.\d+)$/

function safeParseJSON<T = any>(s: string): T | string {
  try { return JSON.parse(s) as T } catch { return s }
}

/**
 * Minimal, schema-agnostic normalizer.
 * - Parses top-level string JSON if needed
 * - Unwraps nested JSON-looking strings
 * - Coerces booleans 'true'/'false'
 * - Optionally coerces specific fields via `typeMap`
 *   (money: "12,345.67" -> 12345.67, number: "42.5" -> 42.5, boolean: 'true' -> true)
 */
export function normaliseJson(input: unknown, typeMap?: TypeMap): any {
  const base = typeof input === 'string' ? safeParseJSON<any>(input) : input
  return walk(base, '')

  function walk(v: any, path: string): any {
    // honor per-path coercion if provided
    if (typeMap && path && typeof v === 'string') {
      const mode = typeMap[path]
      if (mode === 'boolean') return v === 'true' ? true : v === 'false' ? false : v
      if (mode === 'number')  return numRe.test(v.trim()) ? Number(v) : v
      if (mode === 'money')   return moneyRe.test(v.trim()) ? parseFloat(v.replace(/,/g, '')) : v
    }

    if (Array.isArray(v)) return v.map((x, i) => walk(x, `${path}[${i}]`))
    if (v && typeof v === 'object') {
      const out: any = Array.isArray(v) ? [] : {}
      for (const k of Object.keys(v)) {
        const nextPath = path ? `${path}.${k}` : k
        out[k] = walk(v[k], nextPath)
      }
      return out
    }

    // if (typeof v === 'string') {
    //   const s = v.trim()
    //   // safe, global heuristics
    //   if (s === 'true')  return true
    //   if (s === 'false') return false
    //   return v
    // }

    return v
  }
}

/**
 * Build a coercion map from a Vueform-like schema by reading `meta.type` on fields.
 * Supports nested `object/group` fields (with `schema` children).
 *
 * Example field:
 *   { id: 'boat_market_value', meta: { type: 'money' } }
 */
export function buildTypeMapFromSchema(schema: any): TypeMap {
  const map: TypeMap = {}

  const visit = (node: any) => {
    if (!node || typeof node !== 'object') return
    const children = node.schema
    if (children && typeof children === 'object') {
      for (const key of Object.keys(children)) {
        const field = children[key]
        if (field?.id && field?.meta?.type) {
          const t = field.meta.type as Coerce
          if (t === 'money' || t === 'number' || t === 'boolean') {
            map[field.id] = t
          }
        }
        // recurse
        if (field?.schema) visit(field)
      }
    }
  }

  visit(schema)
  return map
}
